'use client';
import { useState } from 'react';
import { createRoot } from 'react-dom/client';

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

import { ClickButton } from './click-button';
import { Button } from './ui/button';

const CONFIRM_DIALOG_VIEWPORT_ID = 'confirm-dialog-viewport';

type ConfirmDialogProps = {
  title: string;
  message: string;
  confirmText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  onClose?: (confirmed: boolean) => void;
};

export type ConfirmWithDialogArgs = Pick<ConfirmDialogProps, 'title' | 'message' | 'confirmText'>;

export function confirmWithDialog({ onClose, ...props }: ConfirmDialogProps) {
  const viewport = document.getElementById(CONFIRM_DIALOG_VIEWPORT_ID);

  if (!viewport) {
    throw new Error('confirm dialog viewport not found');
  } else {
    return new Promise((resolve) => {
      const root = createRoot(viewport);

      root.render(
        <ConfirmDialog
          {...props}
          onClose={(confirmed) => {
            onClose?.(confirmed);

            root.unmount();

            resolve(confirmed);
          }}
        />
      );
    });
  }
}

function ConfirmDialog({ title, message, confirmText = 'Confirm', onConfirm, onCancel, onClose }: ConfirmDialogProps) {
  const [open, setOpen] = useState(true);

  function cancel() {
    onCancel?.();
    onClose?.(false);
    setOpen(false);
  }

  async function confirm() {
    await onConfirm?.();
    onClose?.(true);
    setOpen(false);
  }

  return (
    <Dialog onOpenChange={(open) => !open && cancel()} open={open}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{message}</DialogDescription>
          <div className='flex flex-row-reverse gap-4 pt-4'>
            <ClickButton clickToDisable onClick={confirm}>
              <div className='overflow-hidden text-ellipsis max-w-[15rem]'>{confirmText}</div>
            </ClickButton>
            <Button onClick={cancel} variant='outline'>
              Cancel
            </Button>
          </div>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}

export function ConfirmDialogViewport() {
  return <div id={CONFIRM_DIALOG_VIEWPORT_ID} />;
}
